import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { getLabDataByName } from '../services/labService';
import MainHeader from '../Components/MainHeader';

// ─── Layout ─────────────────────────────────────────
const Container = styled.div`
  min-height: 100vh;
  background-color: #f8f9fa;
  font-family: 'Noto Sans Thai', sans-serif !important;
  * {
    font-family: 'Noto Sans Thai', sans-serif !important;
    font-weight: 400;
    color: #666666;
  }
`;

const TopBar = styled.div`
  background-color: #00BBD3;
  padding: 16px 24px;
  color: white;
  font-size: 20px;
  font-weight: bold;
  font-family: 'Noto Sans Thai', sans-serif !important;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
`;

const Content = styled.div`
  padding: 20px;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: #00BBD3;
  font-size: 15px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  margin-bottom: 16px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

// ─── Impression status card ────────────────────────
const StatusCard = styled.div`
  background: ${props => props.$bgColor || '#00BBD3'};
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
  color: white;
  display: flex;
  align-items: center;
  gap: 14px;
`;

const StatusIcon = styled.img`
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  object-fit: contain;
`;

const StatusInfo = styled.div`
  flex: 1;
`;

const StatusTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: white;
  margin-bottom: 2px;
`;

const StatusValue = styled.div`
  font-size: 17px;
  font-weight: bold;
  color: white;
  word-break: break-word;
`;

// ─── Report card ────────────────────────────────────
const ReportCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  cursor: pointer;
  transition: box-shadow 0.2s ease;

  &:active {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  }
`;

const ReportCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const ReportCardTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #333;
`;

const ReportCardDate = styled.div`
  font-size: 13px;
  color: #999;
`;

const ReportPreview = styled.div`
  font-size: 14px;
  color: #888;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
`;

const ChevronIcon = styled.span`
  color: #00BBD3;
  font-size: 18px;
  margin-left: 8px;
`;

// ─── Detail page ────────────────────────────────────
const SectionCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
`;

const SectionTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid #eee;
`;

const FindingRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 7px 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
  &:first-of-type {
    padding-top: 0;
  }
`;

const FindingDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #00BBD3;
  margin-top: 7px;
  flex-shrink: 0;
`;

const FindingText = styled.div`
  font-size: 14px;
  color: #555;
  line-height: 1.6;
  word-break: break-word;
  overflow-wrap: break-word;
`;

const ImpressionCard = styled.div`
  background: #e8f8f9;
  border-left: 4px solid #00BBD3;
  border-radius: 0 12px 12px 0;
  padding: 16px 18px;
  margin-bottom: 16px;
`;

const ImpressionLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #00BBD3;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
`;

const ImpressionText = styled.div`
  font-size: 17px;
  font-weight: 600;
  color: #333;
`;

const MetaRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
`;

const MetaLabel = styled.div`
  font-size: 14px;
  color: #999;
`;

const MetaValue = styled.div`
  font-size: 14px;
  color: #444;
  font-weight: 600;
  text-align: right;
  word-break: break-word;
`;

// ─── Empty / Loading ────────────────────────────────
const CenterMessage = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #999;
  font-size: 16px;
`;

const formatDisplayDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = (date.getFullYear() + 543).toString().slice(-2);
  return `${day}/${month}/${year}`;
};

// ─── Component ──────────────────────────────────────
const XrayResultsPage = () => {
  const navigate = useNavigate();
  const [xrayReports, setXrayReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadXrayReports = async () => {
      try {
        setLoading(true);
        const response = await getLabDataByName('xray_report');
        const dataArray = response?.data || [];

        const reports = dataArray.map((item, index) => ({
          id: item.id || index,
          time: item.dateadd || '-',
          rawText: item.value,
        }));

        setXrayReports(reports);
      } catch (error) {
        console.error('❌ Error loading xray reports:', error);
        setXrayReports([]);
      } finally {
        setLoading(false);
      }
    };

    loadXrayReports();
  }, []);

  // แปลง raw text จาก API เป็น structured sections
  const parseXrayText = (rawText) => {
    if (!rawText) return { title: '', findings: [], impression: '', signedBy: '', finalizedOn: '' };

    const lines = rawText.split(/\r?\n/).map(l => l.trim()).filter(l => l !== '');

    let title = '';
    const findings = [];
    let impression = '';
    let signedBy = '';
    let finalizedOn = '';

    lines.forEach(line => {
      if (line.startsWith('IMPRESSION:')) {
        impression = line.replace('IMPRESSION:', '').trim();
      } else if (line.startsWith('FinalizedOn:')) {
        finalizedOn = line.replace('FinalizedOn:', '').trim();
      } else if (line.startsWith('X-Ray')) {
        signedBy = line.trim();
      } else if (!title && !line.startsWith('-')) {
        // บรรทัดแรกที่ไม่ใช่ finding คือ title เช่น "ChestfilmPAupright:"
        title = line.replace(/:$/, '').trim();
      } else {
        findings.push(line.startsWith('-') ? line.substring(1).trim() : line);
      }
    });

    return { title, findings, impression, signedBy, finalizedOn };
  };

  return (
    <Container>
      <MainHeader />
      <TopBar>ผลตรวจเอ็กเรย์</TopBar>
      <Content>
        <BackButton onClick={() => navigate(-1)}>
          ← กลับ
        </BackButton>

        {loading ? (
          <CenterMessage>กำลังโหลดข้อมูล...</CenterMessage>
        ) : xrayReports.length === 0 ? (
          <CenterMessage>ไม่พบผลการตรวจเอ็กเรย์</CenterMessage>
        ) : (
          xrayReports.map(report => {
            const parsed = parseXrayText(report.rawText);
            return (
              <ReportCard key={report.id} style={{ cursor: 'default' }}>
                <ReportCardHeader>
                  <ReportCardTitle>
                    {parsed.title || 'ผลการตรวจเอ็กเรย์'}
                  </ReportCardTitle>
                  <ReportCardDate>{formatDisplayDate(report.time)}</ReportCardDate>
                </ReportCardHeader>
                <ReportPreview>
                  {parsed.impression ? `IMPRESSION: ${parsed.impression}` : parsed.findings[0] || '-'}
                </ReportPreview>
              </ReportCard>
            );
          })
        )}
      </Content>
    </Container>
  );
};

export default XrayResultsPage;
